import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Alert,
  Button,
  ButtonGroup,
  Checkbox,
  Chip,
  Field,
  Icon,
  Modal,
  SearchInput,
  StatePanel,
  Textarea,
} from "../components/ui";
import { getFileUrl } from "../core/config/api";
import { shrinkImageForUpload } from "../core/utils/shrinkImage";
import { waBroadcastApi, apiError } from "./waBroadcastApi";
import { clockTime, estimate, formatPhone, normaliseCaption, siteLinks, withoutItemLinks } from "./waBroadcastHelpers";
import { WhatsAppText } from "./WhatsAppText";

import "./wa-broadcast.scss";

const CUSTOM = "custom";

/**
 * "Share to Broadcast": queue one item (listing or request) to a saved list of
 * WhatsApp groups from one of the person's numbers (a picker appears when they
 * have more than one; lists follow the number). The backend paces the actual sending; this
 * sheet only chooses WHAT and TO WHOM, and says up front how long it will take.
 *
 * Sheet on phones, dialog on laptop (Modal variant="auto").
 */
export function SendToWhatsAppSheet({ open, onClose, subjectType, subjectId, label, caption, imageIds = [], noClosingLine = false, prefill = null }) {
  const [state, setState] = useState({ loading: true });
  // "Send again" (Alex, 2026-10-16): the item's last finished broadcast, offered
  // here; or `prefill` when opened from Activity's "Send again".
  const [previous, setPrevious] = useState(null);
  const [usingPrevious, setUsingPrevious] = useState(false);
  // A previous broadcast waiting for the lists to load before its list is picked.
  const pendingRef = useRef(null);
  const [text, setText] = useState("");
  // Set once the person types, so a late template never overwrites their edit.
  const editedRef = useRef(false);
  const [editing, setEditing] = useState(false);
  const [imageId, setImageId] = useState(null);
  // Photos uploaded in this sheet to replace the item's own (newest first).
  const [uploaded, setUploaded] = useState([]);
  const [uploading, setUploading] = useState(false);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const { id } = await waBroadcastApi.uploadPhoto(await shrinkImageForUpload(file));
      setUploaded((ids) => [id, ...ids]);
      setImageId(id);
    } catch (err) {
      setError(apiError(err, "Could not upload that photo — try a JPG or PNG under 10 MB."));
    } finally {
      setUploading(false);
    }
  };
  // Take an uploaded photo away; if it was the chosen one, fall back to the item's first photo.
  const removeUpload = (id) => {
    setUploaded((ids) => ids.filter((x) => x !== id));
    setImageId((current) => (current === id ? imageIds[0] ?? null : current));
  };
  // Its saved list when that still exists, else the very groups it went to.
  const targetPrevious = (p, lists) => {
    if (p.list_id && lists.some((l) => l.id === p.list_id)) {
      setTarget(p.list_id);
    } else {
      setTarget(CUSTOM);
      setPicked(new Set(p.group_ids || []));
    }
  };

  // Open on an earlier broadcast: its caption as written, its photo and its
  // audience. Everything stays editable before sending.
  const applyPrevious = (p) => {
    editedRef.current = true; // a late template must not replace it
    setText(normaliseCaption(p.caption || ""));
    setEditing(false);
    setImageId(p.image_file_id ?? null);
    if (p.image_file_id && !imageIds.includes(p.image_file_id)) {
      setUploaded((ids) => (ids.includes(p.image_file_id) ? ids : [p.image_file_id, ...ids]));
    }
    setUsingPrevious(true);
    if (state.lists) targetPrevious(p, state.lists);
    else pendingRef.current = p;
  };
  const [target, setTarget] = useState(null); // list id or CUSTOM
  const [picked, setPicked] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  // Which number it goes from (undefined = the person's first). Lists and
  // groups belong to a number, so changing it reloads both.
  const [accountId, setAccountId] = useState(undefined);

  useEffect(() => {
    if (!open) return;
    // Exactly what the backend will send (WaBroadcastService::normaliseCaption).
    setText(normaliseCaption(caption));
    editedRef.current = false;
    setImageId(imageIds[0] ?? null);
    setEditing(false);
    setResult(null);
    setError("");
    setQuery("");
    setAccountId(undefined);
    setUploaded([]);
    setPrevious(null);
    setUsingPrevious(false);
    pendingRef.current = null;

    // Opened from Activity's "Send again": start from that broadcast.
    if (prefill) {
      applyPrevious(prefill);
      return undefined;
    }

    // A store with its own template (Owner Admin → Message templates) gets
    // that text instead of the built-in caption — unless editing has begun.
    let alive = true;
    waBroadcastApi.caption(subjectType, subjectId)
      .then((res) => {
        if (alive && res?.caption && !editedRef.current) setText(normaliseCaption(res.caption));
      })
      .catch(() => {}); // the built-in caption stays
    // Offer the item's last finished broadcast to send again.
    waBroadcastApi.lastBroadcast(subjectType, subjectId)
      .then((res) => alive && setPrevious(res?.last || null))
      .catch(() => {});
    return () => { alive = false; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- reset only when the sheet opens

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;

    setPicked(new Set());
    setState({ loading: true });

    waBroadcastApi.overview(accountId)
      .then(async (overview) => {
        const id = overview.account.id;
        const [{ lists }, { groups }] = await Promise.all([waBroadcastApi.lists(id), waBroadcastApi.groups(id)]);
        if (!alive) return;
        const preferred =
          lists.find((l) => l.is_default && l.share_type === subjectType) ||
          lists.find((l) => l.is_default && !l.share_type) ||
          lists.find((l) => !l.share_type || l.share_type === subjectType);
        setTarget(preferred ? preferred.id : CUSTOM);
        if (pendingRef.current) {
          targetPrevious(pendingRef.current, lists);
          pendingRef.current = null;
        }
        // DAO's stores link items to daowatches.com (the backend rewrites the
        // sent text the same way); show the link as it will go out.
        setText((t) => (overview.include_link === false ? withoutItemLinks(t) : siteLinks(t, overview.item_link_base)));
        setState({ loading: false, overview, lists, groups });
      })
      .catch((err) => alive && setState({ loading: false, loadError: apiError(err, "Could not load WhatsApp Broadcast.") }));

    return () => { alive = false; };
  }, [open, accountId, subjectType]);

  const { overview, lists = [], groups = [] } = state;
  // Only numbers that can send (plus the one shown, whatever its state).
  const numbers = (overview?.numbers?.accounts || []).filter((a) => a.status !== "unlinked" || a.id === overview?.account?.id);
  const closers = noClosingLine ? [] : overview?.closing_lines || [];
  const byId = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);

  const chosenIds = target === CUSTOM ? [...picked] : lists.find((l) => l.id === target)?.group_ids || [];
  const chosen = chosenIds.map((id) => byId.get(id)).filter(Boolean);
  const sendable = chosen.filter((g) => !g.unavailable_reason);
  const blocked = chosen.length - sendable.length;
  const eta = estimate(sendable.length, overview);

  const connected = overview?.account?.status === "connected";
  const tooLong = text.length > 1000;
  const canSend = connected && sendable.length > 0 && text.trim() && !tooLong && !sending;

  const togglePick = (id, on) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const send = async () => {
    setSending(true);
    setError("");
    try {
      const body = {
        subject_type: subjectType,
        subject_id: subjectId,
        caption: text,
        image_file_id: imageId,
        ...(target === CUSTOM ? { group_ids: [...picked] } : { list_id: target }),
      };
      setResult(await waBroadcastApi.send(body, overview.account.id));
    } catch (err) {
      setError(apiError(err, "Could not queue the broadcast."));
    } finally {
      setSending(false);
    }
  };

  const footer = result ? (
    <ButtonGroup align="end" stackBelow="sm">
      <Button variant="secondary" size="lg" to="/whatsapp-broadcast" onClick={onClose}>View progress</Button>
      <Button size="lg" onClick={onClose}>Done</Button>
    </ButtonGroup>
  ) : state.loading || state.loadError || !connected ? null : (
    <ButtonGroup align="end" stackBelow="sm">
      <Button variant="secondary" size="lg" onClick={onClose}>Cancel</Button>
      <Button size="lg" loading={sending} disabled={!canSend} leadingIcon={<Icon name="Send" size="sm" />} onClick={send}>
        {sendable.length ? `Send to ${sendable.length} group${sendable.length === 1 ? "" : "s"}` : "Choose groups"}
      </Button>
    </ButtonGroup>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="auto"
      size="lg"
      className="wab-sheet"
      title="Share to Broadcast"
      description={label}
      footer={footer}
    >
      {state.loading ? (
        <StatePanel state="loading" loadingShape="block" compact />
      ) : state.loadError ? (
        <StatePanel state="error" compact title="Could not load WhatsApp Broadcast" description={state.loadError} />
      ) : !connected ? (
        <StatePanel
          state="empty"
          compact
          icon="Phone"
          title="This WhatsApp number isn't linked yet"
          description="Link it once on the WhatsApp Broadcast page (in the menu), then send from here."
          action={{ label: "Open WhatsApp Broadcast", to: "/whatsapp-broadcast", onClick: onClose }}
        />
      ) : result ? (
        <Queued result={result} overview={overview} />
      ) : (
        <div className="wab-compose">
          {(!overview.enabled || overview.account.paused) && (
            <p className="wab-hint">
              <Icon name="Clock" size="xs" /> Sending is paused — this will queue and go out once sending is switched on.
            </p>
          )}
          {error && <Alert tone="error" title={error} />}

          {/* Send again: start from what went out last time, then change anything. */}
          {usingPrevious ? (
            <p className="wab-previous is-using" role="status">
              <Icon name="RefreshCw" size="sm" />
              <span>Same message, photo and groups as last time — change anything below before sending.</span>
            </p>
          ) : previous ? (
            <div className="wab-previous">
              <Icon name="RefreshCw" size="sm" />
              <span>
                Sent before · {clockTime(previous.sent_at)} to{" "}
                <strong>{previous.list_name || `${previous.group_ids.length} group${previous.group_ids.length === 1 ? "" : "s"}`}</strong>
              </span>
              <Button variant="secondary" size="sm" onClick={() => applyPrevious(previous)}>
                Use that message again
              </Button>
            </div>
          ) : null}

          {/* Two columns from tablet up: the message on the left, who gets it on the right. */}
          <div className="wab-compose__cols">
            <div className="wab-compose__preview">
              {/* The message exactly as WhatsApp shows it: same line breaks and
                  spacing, *bold* / _italic_ / ~strike~ rendered, links styled. */}
              <section className="wab-bubble" aria-label="How it looks in WhatsApp">
                {imageId != null && (
                  <RetryImg className="wab-bubble__img" src={`${getFileUrl(imageId)}?height=480`} alt="Photo that will be sent" />
                )}
                {/* The caption shows once: rendered, or as the box being edited (Alex, 2026-10-13). */}
                {editing ? (
                  <Field className="wab-bubble__edit" hint="Line breaks and *bold* go out exactly as typed.">
                    <Textarea
                      value={text}
                      onChange={(e) => { editedRef.current = true; setText(e.target.value); }}
                      minRows={8}
                      maxRows={16}
                      maxLength={1000}
                      fullWidth
                      autoFocus
                      aria-label="Caption"
                    />
                  </Field>
                ) : (
                  <WhatsAppText className="wab-bubble__render" text={normaliseCaption(text)} />
                )}
                {closers.length > 0 && (
                  <p className="wab-bubble__closer">
                    + one of: {closers.map((c) => `“${c}”`).join(" · ")} — or none
                  </p>
                )}
              </section>

              <Button
                variant="secondary"
                size="sm"
                className="wab-edit-caption"
                leadingIcon={<Icon name={editing ? "Check" : "Pencil"} size="sm" />}
                onClick={() => setEditing((on) => !on)}
              >
                {editing ? "Done editing" : "Edit caption"}
              </Button>

              {/* Pick one of the item's photos, upload a different one, or send text only. */}
              <div className="wab-photos" role="radiogroup" aria-label="Photo">
                  {[...uploaded, ...imageIds.slice(0, 8)].map((id) => {
                    const isUpload = uploaded.includes(id);
                    return (
                      <span key={id} className="wab-photo-wrap">
                        <button
                          type="button"
                          role="radio"
                          aria-checked={imageId === id}
                          aria-label={isUpload ? "Uploaded photo" : undefined}
                          className={`wab-photo ${imageId === id ? "is-active" : ""}`}
                          onClick={() => setImageId(id)}
                        >
                          <RetryImg src={`${getFileUrl(id)}?height=120`} alt="" loading="lazy" />
                        </button>
                        {/* An uploaded photo can be taken away again; the item's own stay. */}
                        {isUpload && (
                          <button
                            type="button"
                            className="wab-photo__remove"
                            aria-label="Remove uploaded photo"
                            onClick={() => removeUpload(id)}
                          >
                            <Icon name="X" size="xs" />
                          </button>
                        )}
                      </span>
                    );
                  })}
                  <button
                    type="button"
                    role="radio"
                    aria-checked={imageId == null}
                    className={`wab-photo wab-photo--none ${imageId == null ? "is-active" : ""}`}
                    onClick={() => setImageId(null)}
                  >
                    No photo
                  </button>
                  <label className={`wab-photo wab-photo--upload ${uploading ? "is-busy" : ""}`}>
                    <input
                      type="file"
                      accept="image/*"
                      className="wab-photo__input"
                      disabled={uploading}
                      onChange={(e) => {
                        uploadPhoto(e.target.files?.[0]);
                        e.target.value = "";
                      }}
                    />
                    <Icon name={uploading ? "Clock" : "Upload"} size="sm" />
                    <span>{uploading ? "Uploading…" : "Upload"}</span>
                  </label>
              </div>
            </div>

            <div className="wab-compose__audience">
              {numbers.length > 1 && (
                <div className="wab-audience-pick">
                  <h3 className="wab-subtitle rc-wab-heading">Send from</h3>
                  <div className="wab-chips" role="radiogroup" aria-label="Send from">
                    {numbers.map((a) => (
                      <Chip key={a.id} selected={a.id === overview.account.id} onClick={() => a.id !== overview.account.id && setAccountId(a.id)}>
                        {[a.label, formatPhone(a.phone)].filter(Boolean).join(" · ") || "Not linked yet"}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}

              <div className="wab-audience-pick">
                <h3 className="wab-subtitle rc-wab-heading">Send to</h3>
                <div className="wab-chips">
                  {lists.map((l) => (
                    <Chip key={l.id} selected={target === l.id} onClick={() => setTarget(l.id)} count={l.group_ids.length}>
                      {l.name}
                    </Chip>
                  ))}
                  <Chip selected={target === CUSTOM} onClick={() => setTarget(CUSTOM)} icon={<Icon name="SlidersHorizontal" size="xs" />}>
                    Choose groups
                  </Chip>
                </div>

                {target === CUSTOM && (
                  <div className="wab-custom">
                    <SearchInput
                      size="md"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onClear={() => setQuery("")}
                      placeholder="Find a group"
                      aria-label="Find a group"
                    />
                    {groups.length === 0 ? (
                      <p className="wab-hint">No groups loaded — refresh them on the WhatsApp Broadcast page (in the menu).</p>
                    ) : (
                      <ul className="wab-checks">
                        {groups
                          .filter((g) => g.name.toLowerCase().includes(query.trim().toLowerCase()))
                          .map((g) => (
                            <li key={g.id}>
                              <Checkbox
                                checked={picked.has(g.id)}
                                disabled={!!g.unavailable_reason}
                                onChange={(on) => togglePick(g.id, on)}
                                label={g.name}
                                description={g.unavailable_reason || `${g.participants} members`}
                              />
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {sendable.length > 0 && eta && (
                <div className="wab-summary" aria-live="polite">
                  <Icon name="Clock" size="sm" />
                  <p>
                    <strong>{sendable.length} group{sendable.length === 1 ? "" : "s"}</strong>, one at a time — about{" "}
                    <strong>{eta.minutes} min</strong>.
                    {eta.outsideWindow
                  ? " Outside sending hours — it starts when they open."
                  : eta.later > 0 && ` ${eta.today} go today, the rest after today's limit resets.`}
                    {blocked > 0 && ` ${blocked} in this list can't receive posts and will be skipped.`}
                    {" "}From {[numbers.find((a) => a.id === overview.account.id)?.label, formatPhone(overview.account.phone)].filter(Boolean).join(" · ")}.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * A photo that tries again (twice, a moment apart) if it fails to load: a
 * just-uploaded photo can 404 for an instant while the server prepares it.
 */
function RetryImg({ src, ...rest }) {
  const [tries, setTries] = useState(0);
  useEffect(() => setTries(0), [src]);
  return (
    <img
      {...rest}
      src={tries ? `${src}${src.includes("?") ? "&" : "?"}retry=${tries}` : src}
      onError={() => tries < 2 && setTimeout(() => setTries((t) => t + 1), 800)}
    />
  );
}

function Queued({ result, overview }) {
  const { counts } = result.broadcast;
  const skipped = result.sends.filter((s) => s.status === "skipped");

  return (
    <div className="wab-done">
      <span className="wab-done__icon" aria-hidden="true"><Icon name="CheckCircle2" size="lg" /></span>
      <h3 className="wab-subtitle rc-wab-heading">Queued for {counts.queued} group{counts.queued === 1 ? "" : "s"}</h3>
      <p className="wab-hint">
        {overview.account?.paused || !overview.enabled
          ? "Sending is paused, so nothing goes out yet. Next: switch Sending on on the WhatsApp Broadcast page (in the menu)."
          : overview.in_window
            ? "The first message goes out shortly, then one every minute or so. You can close this — progress shows on the WhatsApp Broadcast page (in the menu)."
            : "It is outside sending hours, so it starts when the window opens. You can close this."}
      </p>
      {skipped.length > 0 && (
        <ul className="wab-sends">
          {skipped.map((s) => (
            <li key={s.id} className="wab-send">
              <span className="wab-send__group">{s.group}</span>
              <span className="wab-send__reason">{s.reason}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SendToWhatsAppSheet;
